import Swiper from 'swiper';
import {
    loadPhotoSwipe,
    loadPhotoSwipeLightbox,
    loadPrkPhotoSwipeThumbnails,
    loadPrkPhotoSwipeVideo,
    loadPrkPhotoSwipeImageAutoSize
} from '../../async/loadPhotoSwipe.js';
import sleep from '../../utils/sleep';
import { logError } from '../../utils/logError';
import { scrollToElement } from '../../common/functions';
import rAF from '../../utils/rAF/index.js';

/**
 * @var {{
 *  navElementClass: string,
 *  navSliderClass: string,
 *  navItemClass: string,
 *  navPrevButtonClass: string,
 *  navNextButtonClass: string,
 *  navDirection: string,
 * }} defaultNavigationOptions
 */
const defaultNavigationOptions = {
    navElementClass: 'product-images-navigation',
    navSliderClass: 'product-images-navigation__slider',
    navItemClass: 'product-images-navigation__item',
    navPrevButtonClass: 'product-images-navigation__nav-button--prev',
    navNextButtonClass: 'product-images-navigation__nav-button--next',
    navDirection: 'vertical',
}

/**
 * @var {{
 *  navElementClass: string,
 *  navSliderClass: string,
 *  navItemClass: string,
 *  navPrevButtonClass: string,
 *  navNextButtonClass: string,
 *  navDirection: string,
 *  zoomImages: boolean,
 *  displayElementClass: string,
 *  imageElementClass: string,
 *  prkVideoClass: string,
 *  onNavigate?: (item: HTMLElement, container: HTMLElement) => void,
 * }} defaultOptions
 */
const defaultOptions = Object.assign({}, defaultNavigationOptions, {
    displayElementClass: 'product-images__display',
    imageElementClass: 'product-images__image',
    zoomImages: true,
    prkVideoClass: 'video',
});

/**
 * Returns selected options by keys
 * @param {{[key: string]: any}} options
 * @param {string[]} keys
 * @return {{[key: string]: any}}
 */
const getOptionsByKeys = function(options, keys) {
    const result = {};

    for (const key of keys) {
        if (key in options) {
            result[key] = options[key];
        }
    }
    
    return result;
}

let productPhotoVideoGalleryCounter = 0;

class ProductPhotoVideoGalleryNavigation {
    /** @var {HTMLElement|null} container */
    container;

    /**
     * @var {{
     *  displayElementClass: string,
     *  zoomElementId: string
     *  imageWrapClass: string,
     * }} options
     */
    options;

    /** @var {HTMLButtonElement|null} prevButton */
    prevButton;

    /** @var {HTMLButtonElement|null} nextButton */
    nextButton;

    /** @var {Swiper|undefined} itemsSlider */
    itemsSlider;

    /** @var {HTMLElement[]} items */
    items = [];

    /** @var {number} activeItemIndex */
    activeItemIndex = 0;

    /**
     * @param {HTMLElement|null} container
     * @param {{
     *  navElementClass?: string,
     *  navSliderClass?: string,
     *  navItemClass?: string,
     *  navPrevButtonClass?: string,
     *  navNextButtonClass?: string,
     *  navDirection?: string,
     * }} options
     */
    constructor(container, options) {
        this.container = container;
        this.options = Object.assign({}, defaultNavigationOptions, options);
        this.onNavButtonClick = this.onNavButtonClick.bind(this);
        this.onNavItemClick = this.onNavItemClick.bind(this);

        this.init();
    }

    init() {
        if (!this.container)
            return;
        
        this.prevButton = this.container.querySelector(`.${this.options.navPrevButtonClass}`);
        this.nextButton = this.container.querySelector(`.${this.options.navNextButtonClass}`);
        this.items = [...this.container.querySelectorAll(`.${this.options.navItemClass}`)];
        this.initSlider();
        this.attachEventListeners();
        this.updateNavButtonsActiveState();
    }

    initSlider() {
        this.itemsSlider = new Swiper(this.container.querySelector(`.${this.options.navSliderClass}`), {
            slidesPerView: 'auto',
            freeMode: true,
            spaceBetween: 0, // Space is controlled via element padding due to buggy behavior of swiper
            slideToClickedSlide: true,
            direction: this.options.navDirection,
            threshold: 5,
            resizeObserver: true,
        });
    }

    attachEventListeners() {
        // Filtering ensures buttons were found (querySelector may return null)
        for (const button of [this.prevButton, this.nextButton].filter(b => b)) {
            button.addEventListener('click', this.onNavButtonClick);
        }

        for (const item of this.items) {
            item.addEventListener('click', this.onNavItemClick);
        }
    }

    /**
     * @param {MouseEvent|PointerEvent|TouchEvent} event 
     */
    onNavButtonClick(event) {
        if (!this.itemsSlider || !this.itemsSlider.slides.length)
            return;

        const { slides } = this.itemsSlider;
        const { currentTarget: navButton } = event;

        if (!navButton)
            return;

        const isNext = navButton === this.nextButton;
        const toSlideIndex = isNext
            ? Math.min(this.activeItemIndex + 1, slides.length - 1)
            : Math.max(this.activeItemIndex - 1, 0);


        if (this.activeItemIndex === toSlideIndex)
            return;

        this.itemsSlider.slideTo(toSlideIndex);
        slides[toSlideIndex].click();
    }

    /**
     * @param {MouseEvent|PointerEvent|TouchEvent} event 
     */
    onNavItemClick(event) {
        event.preventDefault();
        const { currentTarget: slide } = event;
        const clickedItemIndex = this.itemsSlider.slides.indexOf(slide);

        if (clickedItemIndex === this.activeItemIndex)
            return;

        this.activeItemIndex = clickedItemIndex;
        this.updateNavButtonsActiveState();
        this.dispatchNavigateEvent(slide);
    }

    updateNavButtonsActiveState() {
        const { wrapperEl, slides } = this.itemsSlider;
        if (!wrapperEl) return;

        if (wrapperEl.offsetHeight >= wrapperEl.scrollHeight) {
            for (const button of [this.prevButton, this.nextButton]) {
                button?.setAttribute('disabled', 'disabled');
            }
            return;
        }

        if (this.activeItemIndex === 0)
            this.prevButton?.setAttribute('disabled', 'disabled');
        else
            this.prevButton?.removeAttribute('disabled');

        if (this.activeItemIndex === slides.length - 1)
            this.nextButton?.setAttribute('disabled', 'disabled');
        else
            this.nextButton?.removeAttribute('disabled');
    }

    /**
     * @param {HTMLElement} item
     */
    dispatchNavigateEvent(item) {
        this.container.dispatchEvent(new CustomEvent('navigate', { detail: item }));
    }
}

export class ProductPhotoVideoGallery {
    /** @var {number} id */
    id;

    /** @var {HTMLElement} container */
    container;

    /**
     * @var {{
     *  navElementClass: string,
     *  navSliderClass: string,
     *  navItemClass: string,
     *  navPrevButtonClass: string,
     *  navNextButtonClass: string,
     *  navDirection: string,
     *  zoomImages: boolean,
     *  displayElementClass: string,
     *  imageElementClass: string,
     *  prkVideoClass: string,
     *  onNavigate?: (item: HTMLElement, container: HTMLElement) => void,
     * }} options
     */
    options;

    /** @var {HTMLElement|null} display */
    display;

    /** @var {HTMLImageElement|null} image */
    image;

    /** @var {boolean} isImageZoomable */
    isImageZoomable = false;

    /** @var {ProductPhotoVideoGalleryNavigation} nav */
    nav;

    /** @var {PrkPlayer} prkVideoPlayer */
    prkVideoPlayer = null;

    /** @var {number|null} zoomImageRafId is used for moving zoomed image via requestAnimationFrame */
    zoomImageRafId = null;

    /** @var {number|null} zoomedPendingEvent last mousemove event for moving zoomed image via requestAnimationFrame */
    zoomImagePendingEvent = null;

    /**
     * @param {HTMLElement} container
     * @param {{
     *  navElementClass?: string,
     *  navSliderClass?: string,
     *  navItemClass?: string,
     *  navPrevButtonClass?: string,
     *  navNextButtonClass?: string,
     *  navDirection?: string,
     *  zoomImages?: boolean,
     *  displayElementClass?: string,
     *  imageElementClass?: string
     *  onNavigate?: (item: HTMLElement, container: HTMLElement) => void,
     * }} options
     */
    constructor(container, options) {
        this.id = ++productPhotoVideoGalleryCounter;
        this.container = container;
        this.options = Object.assign({}, defaultOptions, options);

        // Bind event handlers to class instance
        this.onNavigate = this.onNavigate.bind(this);
        this.onMouseClickOnImage = this.onMouseClickOnImage.bind(this);
        this.onMouseEnterImage = this.onMouseEnterImage.bind(this);
        this.onMouseLeaveImage = this.onMouseLeaveImage.bind(this);
        this.onMouseMoveOnImage = this.onMouseMoveOnImage.bind(this);
        this.onImageLoad = this.onImageLoad.bind(this);

        this.display = this.container.querySelector(`.${this.options.displayElementClass}`);

        this.init();
    }

    init() {
        this.options.activeNavItemClass = `${this.options.navItemClass}--active`;
        this.options.imageWrapClass = `${this.options.imageElementClass}-wrap`;
        this.options.zoomImageStateClass = `${this.options.imageWrapClass}--zoom`;

        this.nav = new ProductPhotoVideoGalleryNavigation(
            this.container.querySelector(`.${this.options.navElementClass}`),
            getOptionsByKeys(this.options, Object.keys(defaultNavigationOptions))
        );

        this.attachEventListeners();
        this.initShownItem();

        // Preload photoswipe for better UX
        Promise.all([
            loadPhotoSwipe(),
            loadPhotoSwipeLightbox(),
            loadPrkPhotoSwipeThumbnails(),
            loadPrkPhotoSwipeVideo(),
        ]);
    }

    /**
     * Propererly initialize first item with js
     * like PrkVideo has to be initialized with plyr
     */
    async initShownItem() {
        try {
            if (this.nav.items.length) {
                this.showItem(this.nav.items[0]);
            } else {
                const prkVideoElement = this.display.querySelector(`video.${this.options.prkVideoClass}`);
                if (!prkVideoElement)
                    return;

                const PrkPlayer = await import('/js/prk/prk-player').then(m => m.default);
                const player = prkVideoElement;
                player.id = `prk-video-player_${this.id}`;
                this.prkVideoPlayer = new PrkPlayer(player);
            }
        } catch (error) {
            logError(error);
        }
    }

    attachEventListeners() {
        if (this.nav.container) {
            this.nav.container.addEventListener('navigate', this.onNavigate);
        }
    }

    /**
     * @param {CustomEvent} event
     */
    onNavigate(event) {
        const item = event.detail;
        if (!item)
            return;

        try {
            this.showItem(item);
            
            if (this.options.onNavigate instanceof Function)
                this.options.onNavigate(item, this.container);
        } catch (error) {
            logError(error);
        }
    }

    /**
     * @param {PointerEvent|TouchEvent|MouseEvent} event
     */
    onMouseEnterImage(event) {
        const imageWrap = event.currentTarget;

        if (!this.options.zoomImages || !this.isImageZoomable)
            return;

        imageWrap.addEventListener('mouseleave', this.onMouseLeaveImage);
        imageWrap.addEventListener('mousemove', this.onMouseMoveOnImage);

        // Setup animation toward zoom-in
        const targetScale = this.image.naturalWidth / this.image.width;
        this.image.style.transform = `scale(${targetScale})`;

        imageWrap.classList.add(this.options.zoomImageStateClass);
    }

    /**
     * @param {PointerEvent|TouchEvent|MouseEvent} event
     */
    onMouseLeaveImage(event) {
        const imageWrap = event.currentTarget;

        if (!this.options.zoomImages)
            return;

        imageWrap.removeEventListener('mouseleave', this.onMouseLeaveImage);
        imageWrap.removeEventListener('mousemove', this.onMouseMoveOnImage);

        // Start animated zoom out
        this.image.style.transform = '';

        imageWrap.classList.remove(this.options.zoomImageStateClass);
    }

    /**
     * @param {PointerEvent|TouchEvent|MouseEvent} event
     */
    async onMouseClickOnImage(event) {
        event.preventDefault();

        try {
            // Load photoswipe
            const [PhotoSwipe, PhotoSwipeLightbox, PrkPhotoSwipeImageAutoSize, PrkPhotoSwipeThumbnails, PrkPhotoSwipeVideo] = await Promise.all([
                loadPhotoSwipe(),
                loadPhotoSwipeLightbox(),
                loadPrkPhotoSwipeImageAutoSize(),
                loadPrkPhotoSwipeThumbnails(),
                loadPrkPhotoSwipeVideo(),
            ]);

            const options = {
                gallery: this.nav.container,
                children: this.nav.items,
                pswpModule: PhotoSwipe,
            }

            const lightbox = new PhotoSwipeLightbox(options);

            // Use provided source element for zoom animation
            lightbox.addFilter('thumbEl', () => this.image);
            lightbox.addFilter('placeholderSrc', () => this.image)

            new PrkPhotoSwipeImageAutoSize(lightbox);
            new PrkPhotoSwipeVideo(lightbox);
            new PrkPhotoSwipeThumbnails(lightbox, {
                isMobile: document.querySelector('.mob#wrapper') !== null,
            });
            
            // Remove eventlisteners from this.nav.items for them to behave properly
            lightbox.on('close', async () => {
                await sleep(500);
                lightbox.destroy();
            });

            lightbox.init();
            lightbox.loadAndOpen(this.nav.activeItemIndex);
        } catch (error) {
            logError(error);
        }
    }

    /**
     * @param {PointerEvent|TouchEvent|MouseEvent} event
     */
    onMouseMoveOnImage(event) {
        if (!this.options.zoomImages || !this.isImageZoomable) return;

        this.zoomImagePendingEvent = event;
        if (!this.zoomImageRafId) {
            this.zoomImageRafId = rAF(() => {
                this.zoomImageRafId = null;
                this._handleMouseMoveOnImage(this.zoomImagePendingEvent);
            });
        }
    }

    /**
     * @param {PointerEvent|TouchEvent|MouseEvent} event
     */
    _handleMouseMoveOnImage(event) {
        if (!this.options.zoomImages || !this.isImageZoomable) return;

        // Get position of display element
        const { top, left, width, height } = this.display.getBoundingClientRect();

        const translationBorderWidth = Math.max(30, width * 0.1, height * 0.1);
        const moveareaTop = top + translationBorderWidth;
        const moveareaLeft = left + translationBorderWidth;
        const moveareaWidth = width - translationBorderWidth * 2;
        const moveareaHeight = height - translationBorderWidth * 2;

        // Get relative to display element cursor position
        const x = Math.min(Math.max(0, event.clientX - moveareaLeft), moveareaWidth);
        const y = Math.min(Math.max(0, event.clientY - moveareaTop), moveareaHeight);

        // Normalized coordinates [0,1]
        const nx = x / moveareaWidth;
        const ny = y / moveareaHeight;

        // Calculate eased out displacement factor
        const speedX = this.speedFactor(nx);
        const speedY = this.speedFactor(ny);

        // Linear translation (as normal)
        const linearX = nx * 100; // 100 %
        const linearY = ny * 100; // 100 %

        const edgeX = nx < 0.5 ? 0 : 100;
        const edgeY = ny < 0.5 ? 0 : 100;

        // // Calc fullsize image displacement
        const transformOriginX = this.lerp(edgeX, linearX, speedX);
        const transformOriginY = this.lerp(edgeY, linearY, speedY);

        this.image.style.transformOrigin = `${transformOriginX}% ${transformOriginY}%`;
        return;
    }

    /**
     * Parabola ease: speed 0 at edges, 1 at center
     * @param {number} coord must be normalized to [0, 1]
     */
    speedFactor(coord) {
        return 1 - 4 * Math.pow(coord - 0.5, 2); // 0 at 0/1, 1 at 0.5
    }

    /**
     * At edges, stay at 0 or max. (Lerp from edge to linear)
     */
    lerp(a, b, t) {
        return a + (b - a) * t;
    }

    async clearDisplay() {
        if (!this.display)
            return;

        // Prevent memory leaks
        const imageWrap = this.display.querySelector(`.${this.options.imageWrapClass}`);
        if (imageWrap) {
            this.detachImageEvents(imageWrap);
        }

        if (this.prkVideoPlayer) {
            this.prkVideoPlayer.player.destroy();
            this.prkVideoPlayer = null;
            await sleep();
        }

        this.isImageZoomable = false;

        this.display.innerHTML = '';
    }

    /**
     * @param {HTMLElement} element
     */
    displayElement(element) {
        if (!this.display)
            return;

        this.display.appendChild(element);
    }

    /**
     * @param {HTMLElement} navItem
     * @throws {Error}
     */
    showItem(navItem) {
        if (navItem.classList.contains('js-prk-video-thumb'))
            this.showPrkVideo(navItem);
        else if (navItem.classList.contains('js-video-thumb'))
            this.showVideo(navItem);
        else if (navItem.classList.contains('js-product-videos'))
            this.showVideosGroup(navItem);
        else
            this.showImage(navItem);

        // Change active state
        for (const item of this.nav.items) {
            if (item === navItem)
                item.classList.add(this.options.activeNavItemClass);
            else
                item.classList.remove(this.options.activeNavItemClass);
        }
    }

    /**
     * @param {{ src: string, mimeType: string, width: number, height: number }} source
     */
    renderSource(source) {
        return `<source src="${source.src}" type="${source.mimeType}" size="${source.height}"/>`;
    }

    /**
     * @param {Array<{ src: string, mimeType: string, width: number, height: number }>} sources
     * @return {string}
     */
    renderSources(sources) {
        return sources.map(this.renderSource).join('');
    }

    /**
     * @param {{ title: string, sources: Array<{ src: string, mimeType: string, width: number, height: number }>}} videoData
     * @return {HTMLElement}
     */
    createPrkVideoElement(videoData) {
        const player = document.createElement('video');
        player.id = `prk-video-player_${this.id}`;
        player.classList.add('video');
        player.setAttribute('controls', 'controls');
        player.setAttribute('crossorigin', 'crossorigin');
        player.setAttribute('playsinline', 'playsinline');
        player.setAttribute('poster', videoData.poster);
        player.innerHTML = `
            ${this.renderSources(videoData.sources)}
            <p>Ваш браузер не поддерживает видео, обновите его или попробуйте другой.</p>
        `;

        return player;
    }

    /**
     * @param {HTMLElement} navItem
     * @throws {Error} when something is wrong with video data
     */
    async showPrkVideo(navItem) {
        const videoDataElement = navItem.querySelector('script[type="application/json"]');

        if (!videoDataElement)
            throw new Error('Empty video data');

        let videoData = null;
        try {
            videoData = JSON.parse(videoDataElement.textContent);
        } catch (error) {
            throw new Error('Failed parsing video data');
        }

        try {
            const PrkPlayer = await import('/js/prk/prk-player').then(m => m.default);
            await this.clearDisplay();
            const player = this.createPrkVideoElement(videoData);
            this.displayElement(player);
            this.prkVideoPlayer = new PrkPlayer(player);
        } catch (error) {
            // Explicitly log errors in this section of code with extra data
            logError(error, { extra: { videoData } });
        }
    }

    /**
     * @param {string} videoId
     * @return {HTMLElement}
     */
    createIframeVideoElement(videoId) {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('width', this.display.offsetWidth);
        iframe.setAttribute('allowfullscreen', 'allowfullscreen');
        iframe.setAttribute('videoid', videoId);
        iframe.setAttribute('src', this.getYoutubeLink(videoId));

        return iframe;
    }

    /**
     * @param {HTMLElement} navItem 
     * @throws {Error} when video is is empty
     */
    async showVideo(navItem) {
        const videoId = navItem.dataset?.videoId;

        // Error when there is no videoId
        if (!videoId) {
            throw new Error('Video id is missing');
        }

        await this.clearDisplay();
        this.displayElement(this.createIframeVideoElement(videoId));
    }

    /**
     * @param {string} imageSrc
     * @param {string} alt
     * @param {number} width
     * @param {number} height
     * @return {HTMLElement}
     * @throws {Error} when src is empty
     */
    createImageElement(imageSrc, alt = '', width = 0, height = 0) {
        const image = document.createElement('img');
        image.classList.add(this.options.imageElementClass);
        image.src = imageSrc;
        image.alt = alt;

        if (width && height) {
            image.width = width;
            image.height = height;
        }

        return image;
    }

    /**
     * @param {Event} event
     */
    onImageLoad(event) {
        const image = event.currentTarget;

        // Nothing to do when user already switched the image to another
        if (!document.body.contains(image))
            return;

        if (image.naturalWidth < this.display.offsetWidth || image.naturalHeight < this.display.offsetHeight)
            return

        this.isImageZoomable = true;
    }

    /**
     * @param {HTMLElement} imageWrap 
     */
    attachImageEvents(imageWrap) {
        imageWrap.addEventListener('click', this.onMouseClickOnImage);

        if (!this.options.zoomImages)
            return;

        imageWrap.addEventListener('mouseenter', this.onMouseEnterImage);
    }

    /**
     * @param {HTMLElement} imageWrap 
     */
    detachImageEvents(imageWrap) {
        imageWrap.removeEventListener('click', this.onMouseClickOnImage);

        if (!this.options.zoomImages)
            return;

        imageWrap.removeEventListener('mouseenter', this.onMouseEnterImage);
        imageWrap.removeEventListener('mouseleave', this.onMouseLeaveImage);
        imageWrap.removeEventListener('mousemove', this.onMouseMoveOnImage);
    }

    /**
     * @param {HTMLElement} navItem 
     */
    async showImage(navItem) {
        const imageSrc = navItem.dataset?.imageSrc;
        const alt = navItem.dataset?.altText ?? '';
        const width = Number(navItem.dataset?.width ?? 0);
        const height = Number(navItem.dataset?.height ?? 0);

        // Error when there is no imageSrc
        if (!imageSrc) {
            throw new Error('Image src is missing');
        }

        await this.clearDisplay();

        const imageWrap = document.createElement('div');
        imageWrap.classList.add(this.options.imageWrapClass);
        imageWrap.classList.add('aspect-ratio__content');

        const image = this.createImageElement(imageSrc, alt, width, height);
        if (this.options.zoomImages) {
            image.addEventListener('load', this.onImageLoad);
        }
        this.image = image;
        imageWrap.appendChild(image);

        this.attachImageEvents(imageWrap);
        this.displayElement(imageWrap);
    }

    /**
     * Note this method is page specific
     * @param {HTMLElement} navItem 
     */
    async showVideosGroup(navItem) {
        const videosData = this.getVideoData(navItem);

        // Show in place when there is only one video
        if (videosData.length === 1) {
            const videoData = videosData[0];
            switch (videoData.type) {
                case 'prk':
                    try {
                        const PrkPlayer = await import('/js/prk/prk-player').then(m => m.default);
                        await this.clearDisplay();
                        const player = this.createPrkVideoElement(videoData);
                        this.displayElement(player);
                        this.prkVideoPlayer = new PrkPlayer(player);
                    } catch (error) {
                        logError(error, { extra: { videoData } });
                    }
                    return;

                case 'youtube':
                    // Error when there is no videoId
                    if (!videoData.videoId) {
                        logError(new Error('Video id is missing'));
                        return;
                    }

                    await this.clearDisplay();
                    this.displayElement(this.createIframeVideoElement(videoData.videoId));
                    return;
                    
                default:
                    logError(new Error('Unsupported video type'))
                    return;
            }
        }

        // When more than 1 video, scroll to video reviews
        try {
            const videoReviewsTab = document.querySelector(`.tabs-buttons__item#tab-content-videoReview`);
            if (!videoReviewsTab) {
                throw new Error('Video reviews tab not found');
            }

            const tabs = videoReviewsTab.parentNode.parentNode;

            // Click on the video reviews tab.
            // Have to use Dom7 click instead of JS, as only Dom7/jQuery event is being listened to here
            $(videoReviewsTab).click();
            scrollToElement(tabs);
        } catch (error) {
            logError(error, {
                tags: {
                    where: 'ProductPhotoVideoGallery',
                }
            });
        }
    }

    /**
     * Parse element from HTMLElement has inside video
     * @param {HTMLElement} navItem
     * @returns {Object[]}
     */
    getVideoData(navItem) {
        const videosDataElement = navItem.querySelector('script[type="application/json"]');

        if (!videosDataElement)
            return [];

        let videosData = null;

        try {
            videosData = JSON.parse(videosDataElement.textContent);
        } catch (error) {
            return [];
        }

        return videosData ?? [];
    }

    /**
     * @param {string} videoId
     * @returns {string}
     */
    getYoutubeLink(videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=0&autohide=1&rel=0`;
    }
}
